import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { UserAuthorizationsContext, type UserAuthorizationsContextType } from './UserAuthorizationsContext';
import { getUserAuthorizationsQueryOptions } from '../../service/vpc/queryOptions';

import { isNativeWebview } from '@/config/nativeTokenStore';

interface UserAuthorizationsProviderProps {
  children: React.ReactNode;
}

export const UserAuthorizationsProvider: React.FC<UserAuthorizationsProviderProps> = ({ children }) => {
  const { instance } = useMsal();
  const accounts = instance.getActiveAccount();

  const {
    data: userAuthorizations,
    isLoading,
    isPending,
    isError,
    refetch: fetchUserAuthorizations,
  } = useQuery({
    ...getUserAuthorizationsQueryOptions(),
    // Without this gate the request can fire before MSAL has an active account/token, fail,
    // and never automatically retry once auth is ready — leaving auths stuck at null.
    enabled: !!accounts?.idToken || isNativeWebview(),
  });

  const value: UserAuthorizationsContextType = {
    auths: userAuthorizations ?? null,
    loading: isLoading || isPending,
    error: isError ? new Error('Failed to fetch user authorizations') : null,
    refetch: async () => {
      await fetchUserAuthorizations();
    },
  };

  return <UserAuthorizationsContext.Provider value={value}>{children}</UserAuthorizationsContext.Provider>;
};
